import { type ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import tinycolor from 'tinycolor2';
import { ColorSchemeCSSVariable } from '../use-color-scheme';
import { turnToRad } from '../utils';

const GAP = 1;

export interface SectorProps {
  radius: number;
  fraction: number;
  angle: number;
  label: ReactNode;
  customBackgroundColor?: string;
}

export function Sector({
  radius,
  fraction,
  angle,
  label,
  customBackgroundColor
}: SectorProps) {
  const origin = radius;

  const usefulRadius = radius - GAP;
  const innerCircumference = usefulRadius * 2 * Math.PI;

  const finalFraction = fraction - GAP / innerCircumference;

  const arcStartAngle = 2 * Math.PI - turnToRad(finalFraction) / 2;
  const arcEndAngle = arcStartAngle + turnToRad(finalFraction);

  const arcX = origin + usefulRadius * Math.cos(arcStartAngle);
  const arcStartY = origin + usefulRadius * Math.sin(arcStartAngle);
  const arcEndY = origin + usefulRadius * Math.sin(arcEndAngle);

  const largeArcFlag = finalFraction > 0.5 ? 1 : 0;
  const svgPath = [
    `M ${arcX} ${arcStartY}`,
    `A ${usefulRadius} ${usefulRadius} 0 ${largeArcFlag} 1 ${arcX} ${arcEndY}`,
    `L ${origin + GAP} ${origin}`,
    `L ${origin + GAP} ${origin}`,
    'Z'
  ].join(' ');

  const finalAngle = angle + turnToRad(fraction) / 2;

  const customTextColor = useMemo(() => {
    if (!customBackgroundColor) {
      return undefined;
    }
    return tinycolor(customBackgroundColor).isDark() ? '#fff' : '#000';
  }, [customBackgroundColor]);

  return (
    <Petal
      style={{
        clipPath: `path('${svgPath}')`,
        transform: `rotate(${finalAngle}rad)`,
        background: customBackgroundColor
      }}
    >
      <Content
        style={{
          left: origin,
          height: arcEndY - arcStartY
        }}
      >
        <Label
          style={{
            color: customTextColor
          }}
        >
          {label}
        </Label>
      </Content>
    </Petal>
  );
}

const Petal = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  &:nth-child(even) {
    background:
      radial-gradient(
        52.33% 52.33% at 32.75% 33.58%,
        var(${ColorSchemeCSSVariable.SectorBackgroundGradientStart}) 0%,
        var(${ColorSchemeCSSVariable.SectorBackgroundGradientEnd}) 100%
      ),
      linear-gradient(
        0deg,
        var(${ColorSchemeCSSVariable.EvenSectorBackground}) 0%,
        var(${ColorSchemeCSSVariable.EvenSectorBackground}) 100%
      );
  }

  &:nth-child(odd) {
    background:
      radial-gradient(
        52.33% 52.33% at 32.75% 33.58%,
        var(${ColorSchemeCSSVariable.SectorBackgroundGradientStart}) 0%,
        var(${ColorSchemeCSSVariable.SectorBackgroundGradientEnd}) 100%
      ),
      linear-gradient(
        0deg,
        var(${ColorSchemeCSSVariable.OddSectorBackground}) 0%,
        var(${ColorSchemeCSSVariable.OddSectorBackground}) 100%
      );
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 34px 0 42px;
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
`;

const Label = styled.div`
  overflow: hidden;
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(${ColorSchemeCSSVariable.Text});
`;
